import { Button, Form, Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import React from "react"
import { useTranslation } from "react-i18next"
import { FilterSectionOptionsInterface } from "~Components/filter/filterTypes"
import { CardFilterForm } from "~Components/forms/filter/CardFilterForm"
import { CostCenterFilterForm } from "~Components/forms/filter/CostCenterFilterForm"
import { EmployeeFilterForm } from "~Components/forms/filter/EmployeeFilterForm"
import { ExpenseFilterForm } from "~Components/forms/filter/ExpenseFilterForm"
import { InboxFilterForm } from "~Components/forms/filter/InboxFilterForm"
import { PossibleMatchFilterForm } from "~Components/forms/filter/PossibleMatchFilterForm"
import { SubscriptionFilterForm } from "~Components/forms/filter/SubscriptionFilterForm"
import { TransactionFilterForm } from "~Components/forms/filter/TransactionFilterForm"
import { VendorFilterForm } from "~Components/forms/filter/VendorFilterForm"
import { WorkflowFilterForm } from "~Components/forms/filter/WorkflowFilterForm"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { adjustFilterDataForSelectedExpenseKind } from "~Shared/utils/expense.utils"
import { adjustTimeRangeOfDateFields, getFilterTargetPage } from "~Shared/utils/filter.utils"

export interface FilterModalProps {
    table: TablesEnum
    isVisible: boolean
    options: FilterSectionOptionsInterface
    onCancel: (state: false) => void
    onSubmit: (filterData: Store) => Promise<void>
}

/**
 * @deprecated Use only for old redux-based tables.
 */
export const FilterModalLegacy = ({ table, isVisible, options = {}, onCancel, onSubmit }: FilterModalProps) => {
    const { t } = useTranslation()
    const [formInstance] = Form.useForm()

    const handleSubmit = async (values: Store) => {
        let filterValues = { ...values }
        // extend inside this function min/max params whenever we add a new minMax date component to filters
        adjustTimeRangeOfDateFields(filterValues)
        // this condition checks if the filter form is for an expense table.
        if (getFilterTargetPage(table) === TablesEnum.TODO_INVOICE_APPROVAL || table === TablesEnum.POSSIBLE_MATCHES || table === TablesEnum.ATTACH_EXPENSE || TablesEnum.ARCHIVE) {
            filterValues = adjustFilterDataForSelectedExpenseKind(filterValues)
        }

        handleHide()
        await onSubmit(filterValues)
    }

    const handleHide = () => {
        onCancel(false)
    }

    return (
        <Modal
            title={t("action:filter")}
            open={isVisible}
            maskClosable={false}
            onOk={formInstance.submit}
            onCancel={handleHide}
            keyboard
            destroyOnClose
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        formInstance.resetFields()
                        onCancel(false)
                    }}
                >
                    {t("action:cancel")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={() => {
                        formInstance.submit()
                    }}
                >
                    {t("action:apply_filter")}
                </Button>,
            ]}
        >
            {table === TablesEnum.CARDS ? (
                <CardFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.EMPLOYEES ? (
                <EmployeeFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.SUBSCRIPTIONS ? (
                <SubscriptionFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.TRANSACTIONS_INFLOW ||
              table === TablesEnum.TRANSACTIONS_OUTFLOW ||
              table === TablesEnum.TRANSACTIONS_ALL ||
              table === TablesEnum.TRANSACTIONS_ARCHIVE ? (
                <TransactionFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.POSSIBLE_MATCHES ? (
                <PossibleMatchFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.WORKFLOWS ? (
                <WorkflowFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.VENDORS ? (
                <VendorFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.COST_CENTERS ? (
                <CostCenterFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : [TablesEnum.TODO_INVOICE_APPROVAL, TablesEnum.ARCHIVE].includes(getFilterTargetPage(table)) ? (
                <ExpenseFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : [TablesEnum.ATTACH_EXPENSE, TablesEnum.INBOX_INVOICE_MODAL_EXPENSES].includes(table) ? (
                <ExpenseFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : table === TablesEnum.INBOX_INVOICE ? (
                <InboxFilterForm table={table} options={options} formInstance={formInstance} onSubmit={handleSubmit} />
            ) : null}
        </Modal>
    )
}
